// PROD-3310 billing bridge.
// Mirrors: dev-scrips-pm-react src/shared/ui/components/date-range-field.tsx (`DateRangeField`: "YYYY-MM-DD"
// `startDate` / `endDate`, `onChange(start, end)`, min / max, placeholder, label, `commitOn` "select" | "close"; a
// controlled popover over a range calendar, Clear plus the picked range in its foot).
// Wraps:   the bridge `Popover` (Signal DS `AnchoredPopover`) and the bridge `Calendar` (react-day-picker). PM chose NOT
// to use the Signal DS `DateRangePicker`, which closes the moment both ends are set and returns a same-day range on
// the first click, so a range cannot be picked across two clicks; this keeps PM's choice.
// Overrides: none of behaviour; PM's classes are already named utilities. PROD-3310 gap: a DS range field that stays
// open across two clicks.
import { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface DateRangeFieldProps {
  startDate?: string;
  endDate?: string;
  onChange: (startDate: string | undefined, endDate: string | undefined) => void;
  max?: Date;
  min?: Date;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  id?: string;
  className?: string;
  commitOn?: 'select' | 'close';
}

function parseYmd(v?: string): Date | undefined {
  if (!v) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function toYmd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function labelFor(range: DateRange | undefined, placeholder: string): string {
  if (!range?.from) return placeholder;
  return range.to ? `${fmtDate(range.from)} – ${fmtDate(range.to)}` : fmtDate(range.from);
}

export function DateRangeField({
  startDate,
  endDate,
  onChange,
  max,
  min,
  placeholder = 'Select a date range',
  disabled = false,
  label,
  id,
  className,
  commitOn = 'select',
}: DateRangeFieldProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>(undefined);

  const propFrom = parseYmd(startDate);
  const propRange: DateRange | undefined = propFrom ? { from: propFrom, to: parseYmd(endDate) } : undefined;
  const shown = open ? draft : propRange;

  const emit = (r: DateRange | undefined) =>
    onChange(r?.from ? toYmd(r.from) : undefined, r?.to ? toYmd(r.to) : undefined);

  const handleOpenChange = (next: boolean) => {
    if (next) setDraft(propRange);
    else if (commitOn === 'close') emit(draft);
    setOpen(next);
  };

  const handleSelect = (r: DateRange | undefined) => {
    setDraft(r);
    if (commitOn === 'select') emit(r);
  };

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      {label && (
        <label htmlFor={id} className="block text-xs font-medium text-text-muted">
          {label}
        </label>
      )}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger
          render={
            <button
              id={id}
              type="button"
              disabled={disabled}
              className={cn(
                'flex h-8 w-full items-center gap-2 rounded-lg border bg-surface-card px-3 text-size-sm outline-none transition-colors',
                open ? 'border-brand-primary' : 'border-border-strong hover:border-text-muted',
                disabled && 'cursor-not-allowed bg-grey-100',
              )}
            />
          }
        >
          <CalendarIcon className="w-4 h-4 shrink-0 text-text-muted" aria-hidden />
          <span className={cn('truncate', shown?.from ? 'text-text-primary' : 'text-text-muted')}>
            {labelFor(shown, placeholder)}
          </span>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-2">
          <Calendar
            mode="range"
            selected={draft}
            onSelect={handleSelect}
            numberOfMonths={1}
            autoFocus
            startMonth={min}
            endMonth={max}
            disabled={[...(min ? [{ before: min }] : []), ...(max ? [{ after: max }] : [])]}
          />
          <div className="mt-2 flex items-center justify-between border-t border-border-default pt-2 text-size-sm">
            <button
              type="button"
              onClick={() => handleSelect(undefined)}
              className="font-medium text-brand-primary hover:underline"
            >
              Clear
            </button>
            <span className="text-text-muted">{labelFor(draft, '—')}</span>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
